import Autolink from 'components/Autolink'
import moment from 'moment-timezone'
import { inflect } from 'inflection'
import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircle,
  faMapMarkerAlt,
  faUsers
} from '@fortawesome/pro-solid-svg-icons'
import EditButton from 'components/events/EditButton'
import { EventFormat, EventState } from 'graphql/generated'
import language from 'services/Language'
import { EventUser } from './EventUser'
import { EventAttendees } from 'components/modals/EventAttendeesModal'
import RoundedIconButton from 'components/common/RoundedIconButton'
import { useEventContext } from 'context/EventContext'
import Button from 'components/Button'
import {
  faCalendar,
  faCoins,
  faRepeat
} from '@fortawesome/pro-regular-svg-icons'
import Link from 'next/link'
import BaseModal from 'components/modals/BaseModal'
import ModalTrigger from 'components/common/ModalTrigger'

function EventDetails() {
  const { event } = useEventContext()

  return (
    <>
      <div>
        <div className="flex flex-row items-center justify-between">
          <p className="text-xl font-bold text-gray-800">{event.title}</p>
          <EditButton />
        </div>

        {event.series?.slug && (
          <div className="flex items-center space-x-2 mb-2">
            <FontAwesomeIcon icon={faRepeat} className="text-gray-400" />
            <Link href={`/series/${event.series?.slug}`}>
              <p className="text-sm font-light text-text-secondary">
                {event.series?.label}{' '}
                {!event.series?.label.toLowerCase().endsWith('series') &&
                  'Series'}
              </p>
            </Link>
          </div>
        )}

        <div className={'flex items-center space-x-2'}>
          {event.format === EventFormat.IN_PERSON && (
            <>
              <FontAwesomeIcon
                className="text-gray-400"
                size="sm"
                icon={faMapMarkerAlt}
              />{' '}
            </>
          )}
          <p className="text-gray-400 text-sm">{event.formatLabel}</p>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center space-x-2 mb-2">
          <FontAwesomeIcon icon={faCalendar} className="text-gray-800" />
          <p className="text-sm text-gray-800">
            {moment(event.startTime).tz(moment.tz.guess()).format('LLLL z')}
          </p>
        </div>

        {event.isPrivileged &&
          event.recording &&
          event.state !== EventState.ENDED && (
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon
                icon={faCircle}
                className="w-2 h-2 text-gray-800 border border-gray-800 rounded-full p-0.5"
                size="xs"
              />
              <p className="text-sm text-gray-800">
                {moment(event.startTime).isAfter(moment()) &&
                  'Event will be automatically recorded'}

                {moment(event.startTime).isBefore(moment()) &&
                  'Event is being recorded'}
              </p>
            </div>
          )}

        {!!event.location && event.format == EventFormat.IN_PERSON && (
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-800" />
            <p className="text-md text-gray-800">{event.location}</p>
          </div>
        )}

        {!!event.collective && (
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faCoins} className="text-gray-800" />
            <Link href={event.collective.publicUrl}>
              <p className="text-sm text-gray-800">{event.collective.name}</p>
            </Link>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200" />

      <div className="flex justify-between items-center">
        <p className="text-gray-400 flex text-sm font-light">
          <span>
            {`${event.counts.attending} ${inflect(
              language.events.rsvp,
              event.counts.attending
            )}`}
          </span>

          <span>&nbsp;{`\u00B7`}&nbsp;</span>

          {event.capacity > 0
            ? `${event.capacity - event.counts.attending} ${inflect(
                'Tickets',
                event.capacity - event.counts.attending
              )} Left`
            : `Unlimited Tickets`}
        </p>

        <div className="flex">
          <ModalTrigger
            trigger={onClick =>
              event.canEdit || event.thumb?.length ? (
                <RoundedIconButton
                  icon={faUsers}
                  size="xs"
                  color="lightGray"
                  onClick={onClick}
                />
              ) : (
                <Button
                  label={language.events.view_guestlist}
                  icon={faUsers}
                  color="white"
                  rounded="lg"
                  size="xs"
                  labelClassName="text-gray-500"
                  onClick={onClick}
                />
              )
            }
          >
            <BaseModal disableBackdropClick>
              <EventAttendees event={event} eventContext="event" />
            </BaseModal>
          </ModalTrigger>
        </div>
      </div>

      <div className="border-t border-gray-200" />

      <div>
        <p className="text-gray-400 text-sm font-light">Hosted by:</p>
        <EventUser
          user={event.user}
          event={event}
          key={`host`}
          roundButton={event.canEdit || !!event.thumb?.length}
        />
      </div>

      {event.speakers.length > 0 && (
        <div className="">
          <p className="text-gray-400 text-sm font-light">Speakers:</p>
          {event.speakers.map((speaker, index) => (
            <EventUser user={speaker} event={event} key={`speaker-${index}`} />
          ))}
        </div>
      )}

      <div className="border-gray-200 border-b" />

      <div className="space-y-4">
        <Autolink lightWeight>
          {event.description.replace(/\n/g, '<br/>')}
        </Autolink>
      </div>
    </>
  )
}

export default EventDetails
